'use client';

import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'next/navigation';
import { Alert, Box, Typography } from '@mui/material';
import { EVENT_LEAD_FORM_GRID_SX, EventLeadFormSkeleton } from '@/components/EventLeadForm';
import MarketingInteriorLayout, {
  MARKETING_FORM_COLUMN_INNER_SX,
  MARKETING_FORM_COLUMN_SX,
  MARKETING_FORM_HERO_COLUMN_SX,
} from '@/components/home-landing/MarketingInteriorLayout';
import {
  FORM_PAGE_DESKTOP_BP,
  heroTextLegibility,
} from '@/components/design-studio/design-studio-glass';
import CareersApplicationForm from './CareersApplicationForm';

const PAGE_TITLE = 'Work At NIICO';
const DEFAULT_SUBTITLE =
  'We are always looking for talented and hardworking individuals to grow with us here at NIICO. Please let us know why you love this industry and why you want to be a part of our NIICO team.';
const SUCCESS_MESSAGE =
  'Thank you! Your application has been submitted successfully. Our team will review your resume and be in touch if there is a fit.';

function CareersFormShell({ children }) {
  return (
    <MarketingInteriorLayout heroBackground formGrid formGridSx={EVENT_LEAD_FORM_GRID_SX}>
      {children}
    </MarketingInteriorLayout>
  );
}

function CareersCopyColumn({ title, subtitle }) {
  return (
    <Box
      sx={{
        ...MARKETING_FORM_HERO_COLUMN_SX,
        textAlign: { xs: 'center', [FORM_PAGE_DESKTOP_BP]: 'left' },
      }}
    >
      <Typography
        component="h1"
        sx={{
          ...heroTextLegibility,
          fontWeight: 600,
          fontSize: {
            xs: 'clamp(1.75rem, 5vw, 2.5rem)',
            [FORM_PAGE_DESKTOP_BP]: '2.75rem',
            xl: '3.25rem',
          },
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          mb: { xs: 2, [FORM_PAGE_DESKTOP_BP]: 1.25 },
        }}
      >
        {title}
      </Typography>
      <Typography
        component="p"
        sx={{
          ...heroTextLegibility,
          fontWeight: 400,
          fontSize: { xs: '1rem', [FORM_PAGE_DESKTOP_BP]: '1.0625rem' },
          lineHeight: { xs: 1.65, [FORM_PAGE_DESKTOP_BP]: 1.55 },
          maxWidth: { [FORM_PAGE_DESKTOP_BP]: 420, xl: 480 },
          mx: { xs: 'auto', [FORM_PAGE_DESKTOP_BP]: 0 },
          opacity: 0.95,
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
}

function CareersFormMessageColumn({ severity, children }) {
  return (
    <>
      <CareersCopyColumn title={PAGE_TITLE} subtitle={DEFAULT_SUBTITLE} />
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
          justifyContent: { xs: 'stretch', [FORM_PAGE_DESKTOP_BP]: 'center' },
        }}
      >
        <Alert
          severity={severity}
          sx={{
            width: '100%',
            maxWidth: { [FORM_PAGE_DESKTOP_BP]: 520, xl: 560 },
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            color: 'common.white',
            border: '1px solid rgba(255, 255, 255, 0.35)',
            backdropFilter: 'blur(12px)',
            '& .MuiAlert-icon': { color: 'common.white' },
          }}
        >
          {children}
        </Alert>
      </Box>
    </>
  );
}

CareersFormShell.propTypes = {
  children: PropTypes.node.isRequired,
};

CareersCopyColumn.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

CareersFormMessageColumn.propTypes = {
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']).isRequired,
  children: PropTypes.node.isRequired,
};

function CareersFormPageContent() {
  const searchParams = useSearchParams();
  const isSubmitted = searchParams.get('submitted') === '1';

  if (isSubmitted) {
    return (
      <CareersFormShell>
        <CareersFormMessageColumn severity="success">{SUCCESS_MESSAGE}</CareersFormMessageColumn>
      </CareersFormShell>
    );
  }

  return (
    <CareersFormShell>
      <CareersCopyColumn title={PAGE_TITLE} subtitle={DEFAULT_SUBTITLE} />
      <Box sx={MARKETING_FORM_COLUMN_SX}>
        <Box sx={MARKETING_FORM_COLUMN_INNER_SX}>
          <CareersApplicationForm variant="glass" />
        </Box>
      </Box>
    </CareersFormShell>
  );
}

export default function CareersFormPage() {
  return (
    <Suspense fallback={<EventLeadFormSkeleton />}>
      <CareersFormPageContent />
    </Suspense>
  );
}
